import { Component, ViewChild, effect, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Popover } from 'primeng/popover';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BehaviorSubject, Subject } from 'rxjs';
import { FilterDependentConfig, FilterFieldConfig } from 'src/app/models/general';
import { TranslationPipe } from 'src/app/pipes/translation/translation-pipe';
import { TranslationService } from 'src/app/services/configuration/translation-service';
import { DateTimeService } from 'src/app/services/date-time/date-time-service';
import { ToastService } from 'src/app/services/toast/toast-service';
import { Preview } from 'src/app/shared/preview/preview';
import { AppButton } from '../button/app-button';

@Component({
  selector: 'app-filter',
  imports: [
    AppButton,
    Popover,
    IconField,
    InputIcon,
    ToggleSwitchModule,
    TranslationPipe,
    Select,
    DatePickerModule,
    FormsModule,
    RadioButtonModule
  ],
  templateUrl: './filter.html',
  styleUrl: './filter.scss'
})
export class AppFilter {
  @ViewChild('filter') filter!: Popover;

  filterFields = input<FilterFieldConfig[]>([]);
  dateField = input<boolean>(true);
  timeField = input<boolean>(true);
  customTime = input<string>();
  dateTargetField = input<boolean>(true);
  filterData = input<any[]>();

  apply = output<{ key: string; value: any; matchType?: number; dataType?: number }[]>();
  reset = output<void>();
  fieldChange = output<any>();

  dialogService = inject(DialogService);
  dateTimeService = inject(DateTimeService);
  toastService = inject(ToastService);
  translationService = inject(TranslationService);

  options: Record<string, any[]> = {};
  selected: Record<string, any> = {};
  selectionDialogRef!: DynamicDialogRef<any>;
  steps$ = new BehaviorSubject<any[]>([]);
  searchSubject$ = new Subject<any>();
  showParent: boolean = false;
  normalizedFilterFields: FilterFieldConfig[] = [];
  filterIcon: string = 'filter-icon';
  dateTarget: 'createdAt' | 'updatedAt' = 'createdAt';

  constructor() {
    // Normalize fields
    effect(() => {
      this.normalizedFilterFields = (this.filterFields() ?? []).map((f) => {
        const inst = Object.assign(new FilterFieldConfig(), f);
        if (inst.dependent) inst.dependent = Object.assign(new FilterDependentConfig(), inst.dependent);
        return inst;
      });
    });

    // Map data into options
    effect(() => {
      const data = this.filterData();
      const fields = this.normalizedFilterFields;
      if (!data?.length || !fields.length) return;
      const nextOptions: Record<string, any[]> = {};
      fields.forEach((f) => {
        const val = f.dependent?.value ?? f.value;
        const entry = data.find((d) => d[val])?.[val];
        const raw = Array.isArray(entry) ? entry : [];
        if (f.type === 'dropdown' || f.type === 'selection' || f.dependent?.type === 'dropdown' || f.dependent?.type === 'selection') {
          nextOptions[val] = this.normalizeOptions(raw, f.displayKey);
        }
      });

      this.options = nextOptions;
    });
  }

  normalizeOptions(raw: any[] = [], displayKey?: string) {
    return (raw ?? []).map((item) => {
      const label = displayKey ? item?.[displayKey]?.trim() : item?.description?.trim() || item?.name?.trim() || '';
      // const label = item?.[displayKey]?.trim() || item?.description?.trim() || '';
      return {
        id: item.id ?? label, // fallback for ngModel binding
        label,
        description: label,
        customValue: item.customValue
      };
    });
  }

  openSelectionPopup(field: any) {
    // For both main and dependent selections, always use the field's value
    const key = field.value;
    if (!key) return;
    // Flatten filterData to a single map for easier lookup
    const dataMap: Record<string, any[]> = {};
    (this.filterData() ?? []).forEach((obj) => {
      Object.keys(obj).forEach((value) => {
        dataMap[value] = Array.isArray(obj[value]) ? obj[value] : [];
      });
    });
    const raw = dataMap[key] ?? [];
    if (!raw.length) return;
    // Map for hierarchical display
    const mapped = raw.map((item: any) => ({
      ...item,
      label: item.description ?? item.name ?? '',
      children: (item.children ?? []).map((c: any) => ({
        ...c,
        label: c.description ?? c.name ?? ''
      }))
    }));
    this.steps$.next(mapped);
    // Open dialog
    this.selectionDialogRef = this.dialogService.open(Preview, {
      data: {
        steps$: this.steps$,
        searchSubject$: this.searchSubject$,
        showPreviewLabel: false
      },
      width: '720px',
      height: 'auto',
      header: this.translationService.translate('SELECT_ITEM'),
      closable: true,
      modal: true,
      dismissableMask: true
    });

    this.selectionDialogRef.onClose.subscribe((picked) => {
      if (picked) {
        this.selected[key] = picked.description ?? picked.name ?? picked.label ?? '';
        this.emitFieldChange(key, this.selected[key]);
      }
    });
  }

  applyCustomValue(field: FilterFieldConfig, rawValue: any): any {
    if (field.type !== 'dropdown' && field.type !== 'selection') {
      return rawValue;
    }
    const key = field.dependent?.value ?? field.value;
    const opts = this.options[key] ?? [];
    const found = opts.find((o) => o.label === rawValue || o.description === rawValue || o.id === rawValue);
    return found?.customValue ?? rawValue;
  }

  onApply() {
    const filters = [];
    const dateRange = this.selected['date'];
    const timeRange = this.selected['time'];
    this.normalizedFilterFields.forEach((f) => {
      if (f.hide) return;
      if (f.type === 'toggle' && f.dataType === undefined) {
        f.dataType = 1;
      }
      if (f.dataType === undefined) {
        f.dataType = 0;
      }
      if (f.type === 'toggle') {
        if (this.selected[f.value] === undefined) {
          this.selected[f.value] = false;
        }
      }
    });
    if (dateRange?.from) {
      filters.push({
        key: this.customTime() ?? this.dateTarget,
        value: this.dateTimeService.convertToUtcWithJwtOffset(dateRange.from, timeRange?.from ?? null),
        matchType: 8,
        dataType: 2
      });
    }
    if (dateRange?.to) {
      let time = timeRange?.to;
      if (!time) {
        time = new Date(0, 0, 0, 23, 59, 59);
      }
      filters.push({
        key: this.customTime() ?? this.dateTarget,
        value: this.dateTimeService.convertToUtcWithJwtOffset(dateRange.to, time ?? null, true),
        matchType: 6,
        dataType: 2
      });
    }
    for (const f of this.normalizedFilterFields) {
      if (f.hide) continue;
      const raw = this.selected[f.value] ?? (f.dependent ? this.selected[f.dependent.value] : null);
      if (raw === null || raw === undefined || raw === '') continue;
      const value = this.applyCustomValue(f, raw);
      const realKey = f.value === 'toggleStatus' ? 'status' : f.value;
      if (f.type === 'toggle') {
        if (this.selected[f.value] === undefined || this.selected[f.value] === null) {
          this.selected[f.value] = false;
        }
        if (this.shouldShowDependent(f) && f.dependent) {
          const depKey = f.dependent.value;
          const depVal = this.selected[depKey];

          if (depVal !== null && depVal !== undefined && depVal !== '') {
            filters.push({
              key: depKey,
              value: depVal,
              matchType: f.dependent.matchType,
              dataType: f.dependent.dataType
            });
          }
          filters.push({
            key: realKey,
            value: value ? f.onValue : f.offValue,
            matchType: f.matchType,
            dataType: f.dataType
          });
        } else {
          filters.push({
            key: realKey,
            value: value ? f.onValue : f.offValue,
            matchType: f.matchType,
            dataType: f.dataType
          });
        }
        continue;
      }
      if (f.type === 'date') {
        const dateVal = this.selected[f.value];
        if (dateVal) {
          filters.push({
            key: f.value,
            value: this.dateTimeService.utcFormat(dateVal),
            matchType: 3,
            dataType: 2
          });
        }
        continue;
      }
      filters.push({
        key: f.dependent?.value ?? realKey,
        value: value,
        matchType: f.matchType,
        dataType: f.dependent ? 0 : f.dataType
      });
    }

    this.apply.emit(filters);
    this.filter.hide();
  }

  onReset() {
    this.selected = {};
    this.reset.emit();
    this.filter.hide();
  }

  emitFieldChange(key: string, value: any) {
    if (typeof value === 'boolean') {
      return this.fieldChange.emit([key, value]);
    }
    const data = this.filterData();
    if (!data?.length) return;
    const field = this.normalizedFilterFields.find((f) => f.value === key || f.dependent?.value === key);
    const displayKey = field?.displayKey;
    const group = data.find((d) => d[key]);
    if (!group) return;
    const list = group[key];
    const selectedItem = list.find((f: any) => {
      return f?.[displayKey!] === value;
    });
    if (selectedItem) {
      this.fieldChange.emit([key, selectedItem]);
    }
  }

  onToggleFilter(event: Event) {
    this.normalizedFilterFields.forEach((f) => {
      if (f.type === 'toggle' && this.selected[f.value] === undefined) {
        this.selected[f.value] = false;
      }
    });
    this.filter.toggle(event);
  }

  ensureRange(value: string) {
    if (!this.selected[value]) {
      this.selected[value] = { from: null, to: null };
    }
    return this.selected[value];
  }

  validateRange() {
    const time = this.selected['time'];
    if (!time || !time.from || !time.to) return;
    const date = this.selected['date'];
    if (!date?.from || !date?.to) return;

    const sameDay =
      date.from.getFullYear() === date.to.getFullYear() &&
      date.from.getMonth() === date.to.getMonth() &&
      date.from.getDate() === date.to.getDate();
    if (sameDay && time.to < time.from) {
      time.to = this.dateTimeService.toDateObject(time.from);
      // time.to = this.dateTimeService.convertToDateFormat(time.from);
    }
  }

  combineDateTime(date: Date | null, time: Date | null): Date | null {
    if (!date) return null;
    const localYear = date.getFullYear();
    const localMonth = date.getMonth();
    const localDay = date.getDate();
    if (!time) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    }
    const localHours = time.getHours();
    const localMinutes = time.getMinutes();
    const localSeconds = time.getSeconds();
    const localMs = time.getMilliseconds();
    return new Date(localYear, localMonth, localDay, localHours, localMinutes, localSeconds, localMs);
  }

  shouldShowDependent(f: FilterFieldConfig): boolean {
    const val = !!this.selected[f.value];
    return f.reverse ? val : !val;
  }
}
